import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Zap,
  Sun,
  Moon,
  Monitor,
  LogOut,
  User,
  Truck,
  Clock,
  AlertTriangle,
  FolderOpen,
  Plus,
  Calendar,
  Trash2,
  CheckCircle,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getStatusLabel } from './ui/StatusBadge';
import { useTheme } from '../contexts/ThemeContext';
import { isNotificationEnabled, requestNotificationPermission } from '../services/notificationService';
import { getAllProjects } from '../services/projectService';
import { Project } from '../types';

const BellIcon = ({ size = 16, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </svg>
);

interface NavItem {
  label: string;
  icon: React.FC<any>;
  to?: string;
  filter?: string;
}

type FilterCounts = Record<string, number>;

/** Same rule as the desktop home's "Behöver åtgärd" lane. */
const needsAttention = (p: Project, today: Date): boolean => {
  if (p.status === 'avslutad' || p.status === 'avbruten') return false;
  if (p.needsReview) return true;
  if (!p.contact?.name?.trim() || !p.contact?.phone?.trim()) return true;
  const pickup = p.dates.scheduledPickup?.toDate() ?? null;
  const evaluation = p.dates.evaluation?.toDate() ?? null;
  if (!pickup && evaluation && evaluation < today) return true;
  if (p.status === 'värderad' && !pickup) return true;
  return !!pickup && (p.status === 'planerad' || p.status === 'värderad') && pickup < today;
};

const navClass = (active: boolean) =>
  `flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${
    active
      ? 'bg-accent-50 text-accent-700 dark:bg-accent-900/30 dark:text-accent-300'
      : 'text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)] hover:text-[var(--text-primary)]'
  }`;

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [counts, setCounts] = useState<FilterCounts | null>(null);

  useEffect(() => {
    setNotificationsEnabled(isNotificationEnabled());
  }, []);

  // Live counts per filter, so the rail says how much work sits behind each
  // entry. Refetched on every navigation; the collection is small.
  useEffect(() => {
    let cancelled = false;
    getAllProjects()
      .then((all: Project[]) => {
        if (cancelled) return;
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const c: FilterCounts = { active: 0, bostad: 0, värderad: 0, planerad: 0, väntar: 0, needs_attention: 0, avslutad: 0, avbruten: 0 };
        all.forEach(p => {
          if (p.status !== 'avslutad' && p.status !== 'avbruten') c.active++;
          if (p.status === 'bostad' || p.status === 'hem') c.bostad++;
          else if (c[p.status] !== undefined) c[p.status]++;
          if (needsAttention(p, today)) c.needs_attention++;
        });
        setCounts(c);
      })
      .catch(err => console.error('Sidebar counts failed:', err));
    return () => { cancelled = true; };
  }, [location.key]);

  const search = new URLSearchParams(location.search);
  const currentFilter = search.get('filter');
  const inTrash = search.get('trash') === '1';
  const isHome = location.pathname === '/' || location.pathname === '/projects';

  const handleFilterNav = (filter: string) => {
    localStorage.setItem('stadsauktion_filter', filter);
    navigate(`/?filter=${filter}`);
  };

  // Same wording and same set as the mobile filter pills.
  const filterNav: NavItem[] = [
    { label: 'Aktiva', icon: FolderOpen, filter: 'active' },
    { label: getStatusLabel('bostad'), icon: Plus, filter: 'bostad' },
    { label: getStatusLabel('värderad'), icon: Truck, filter: 'värderad' },
    { label: getStatusLabel('planerad'), icon: Calendar, filter: 'planerad' },
    { label: getStatusLabel('väntar'), icon: Clock, filter: 'väntar' },
    { label: 'Behöver åtgärd', icon: AlertTriangle, filter: 'needs_attention' },
  ];

  // Closed projects stay reachable: photos and notes may be needed long after
  // a pickup if something is disputed with the homeowner.
  const archiveNav: NavItem[] = [
    { label: 'Avslutade', icon: CheckCircle, filter: 'avslutad' },
    { label: 'Avbrutna', icon: X, filter: 'avbruten' },
  ];

  const themeOptions: { value: 'light' | 'dark' | 'system'; icon: React.FC<any>; label: string }[] = [
    { value: 'light', icon: Sun, label: 'Ljust' },
    { value: 'dark', icon: Moon, label: 'Mörkt' },
    { value: 'system', icon: Monitor, label: 'Auto' },
  ];

  return (
    <aside className="hidden lg:flex lg:flex-col lg:w-[240px] lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 bg-[var(--bg-surface)] border-r border-[var(--border-default)]">
      {/* Brand */}
      <div className="flex items-center gap-2.5 h-14 px-5 border-b border-[var(--border-default)]">
        <Link to="/" className="flex items-center gap-2.5 h-[36px]" aria-label="Stadsauktion, till projekten">
          <div className="w-8 h-8 rounded-lg bg-accent-600 flex items-center justify-center">
            <span className="text-white text-sm font-bold" aria-hidden="true">S</span>
          </div>
          <span className="text-sm font-semibold text-[var(--text-primary)]">Stadsauktion</span>
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-3" aria-label="Huvudnavigering">
        <Link to="/" className={navClass(isHome && !currentFilter && !inTrash)}>
          <Home size={18} />
          Projekt
        </Link>

        {/* Filter shortcuts with live counts */}
        <div className="mt-6">
          <p className="px-3 mb-2 text-[12px] font-semibold uppercase tracking-[0.04em] text-[var(--text-secondary)]">
            Filter
          </p>
          <div className="space-y-0.5">
            {filterNav.map(item => {
              const Icon = item.icon;
              const isActive = isHome && !inTrash && currentFilter === item.filter;
              const count = counts?.[item.filter!];
              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => handleFilterNav(item.filter!)}
                  aria-current={isActive ? 'page' : undefined}
                  className={navClass(isActive)}
                >
                  <Icon size={16} />
                  <span className="flex-1 truncate">{item.label}</span>
                  {count !== undefined && (
                    <span className={`text-[12px] tabular-nums ${isActive ? 'text-accent-700 dark:text-accent-300' : 'text-[var(--text-secondary)]'}`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6">
          <p className="px-3 mb-2 text-[12px] font-semibold uppercase tracking-[0.04em] text-[var(--text-secondary)]">
            Arkiv
          </p>
          <div className="space-y-0.5">
            {archiveNav.map(item => {
              const Icon = item.icon;
              const isActive = isHome && !inTrash && currentFilter === item.filter;
              const count = counts?.[item.filter!];
              return (
                <button key={item.label} type="button" onClick={() => handleFilterNav(item.filter!)} aria-current={isActive ? 'page' : undefined} className={navClass(isActive)}>
                  <Icon size={16} />
                  <span className="flex-1 truncate">{item.label}</span>
                  {count !== undefined && <span className={`text-[12px] tabular-nums ${isActive ? 'text-accent-700 dark:text-accent-300' : 'text-[var(--text-secondary)]'}`}>{count}</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6 space-y-0.5">
          <Link to="/ai-analysis" className={navClass(location.pathname.startsWith('/ai-analysis'))}>
            <Zap size={16} />
            AI Analys
          </Link>
        </div>
      </nav>

      {/* Bottom section */}
      <div className="border-t border-[var(--border-default)] p-3 space-y-3">
        <Link to="/?trash=1" className={navClass(isHome && inTrash)} aria-current={isHome && inTrash ? 'page' : undefined}>
          <Trash2 size={16} />
          Papperskorg
        </Link>
        <div className="flex items-center bg-[var(--bg-surface-alt)] rounded-lg p-0.5" role="group" aria-label="Tema">
          {themeOptions.map(opt => {
            const Icon = opt.icon;
            const isActive = theme === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setTheme(opt.value)}
                aria-pressed={isActive}
                aria-label={opt.label}
                className={`flex-1 flex items-center justify-center gap-1.5 h-[32px] rounded-md text-xs font-medium transition-colors ${
                  isActive
                    ? 'bg-[var(--bg-surface)] text-[var(--text-primary)] shadow-sm'
                    : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                }`}
                title={opt.label}
              >
                <Icon size={14} />
                <span className="hidden xl:inline">{opt.label}</span>
              </button>
            );
          })}
        </div>

        {!notificationsEnabled && (
          <button
            type="button"
            onClick={async () => {
              const result = await requestNotificationPermission();
              setNotificationsEnabled(result.enabled);
            }}
            className="flex items-center gap-2 w-full px-3 h-[32px] rounded-lg text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)] hover:text-[var(--text-primary)] transition-colors"
          >
            <BellIcon size={14} />
            <span>Aktivera notiser</span>
          </button>
        )}

        <div className="flex items-center gap-2 px-2">
          <div className="w-7 h-7 rounded-full bg-[var(--bg-surface-alt)] flex items-center justify-center flex-shrink-0">
            <User size={12} className="text-[var(--text-secondary)]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-[var(--text-primary)] truncate">{user?.email}</p>
          </div>
          <button
            type="button"
            onClick={signOut}
            className="h-[32px] w-[32px] flex items-center justify-center rounded-lg text-[var(--text-secondary)] hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            title="Logga ut"
            aria-label="Logga ut"
          >
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
